import React from "react";
import clsx from "clsx";
import { Slider } from "@radix-ui/themes";

type BusinessPricesForServicessSliderProps = {
  value: number;
  min: number;
  max: number;
  step: number;
  isLocked: boolean;
  onValueChange: (value: number) => void;
};

export function BusinessPricesForServicessSlider({
  value,
  min,
  max,
  step,
  isLocked,
  onValueChange,
}: BusinessPricesForServicessSliderProps) {
  const percent = ((value - min) / (max - min)) * 100;
  const centerPercent = (((min + max) / 2 - min) / (max - min)) * 100;
  const fillLeft = Math.min(percent, centerPercent);
  const fillWidth = Math.abs(percent - centerPercent);
  const isLeft = percent < centerPercent;

  const sliderStyle = {
    "--slider-fill-left": `${fillLeft}%`,
    "--slider-fill-width": `${fillWidth}%`,
  } as React.CSSProperties;

  const renderLabel = (val: number) => {
    if (val === min)
      return <span className="text-[0.5625rem] font-bold text-white/50 uppercase">min</span>;
    if (val === max)
      return <span className="text-[0.5625rem] font-bold text-white/50 uppercase">max</span>;
    return null;
  };

  return (
    <div className={clsx("flex flex-col w-full", isLocked && "pointer-events-none")}>
      <div className="relative">
        <Slider
          variant="soft"
          value={[value]}
          min={min}
          max={max}
          step={step}
          onValueChange={(v: number[]) => onValueChange(v[0])}
          style={sliderStyle}
          className={clsx(
            "cursor-pointer",
            "[&_.rt-SliderRange]:hidden",
            "[&_.rt-SliderTrack::before]:left-[var(--slider-fill-left)]",
            "[&_.rt-SliderTrack::before]:w-[var(--slider-fill-width)]",
            // трек
            "[&_.rt-SliderTrack]:relative",
            "[&_.rt-SliderTrack]:h-1",
            "[&_.rt-SliderTrack]:bg-[#191919]",
            "[&_.rt-SliderTrack]:rounded-4",
            // кастомная заливка
            "[&_.rt-SliderTrack::before]:content-['']",
            "[&_.rt-SliderTrack::before]:absolute",
            "[&_.rt-SliderTrack::before]:top-0",
            "[&_.rt-SliderTrack::before]:h-full",
            "[&_.rt-SliderTrack::before]:bg-gradient-lightBlue",
            isLocked && "[&_.rt-SliderTrack::before]:hidden",
            // углы заливки
            isLeft
              ? "[&_.rt-SliderTrack::before]:rounded-l-4 [&_.rt-SliderTrack::before]:rounded-r-none"
              : "[&_.rt-SliderTrack::before]:rounded-r-4 [&_.rt-SliderTrack::before]:rounded-l-none",
            // вертикальная полоска в центре
            "[&_.rt-SliderTrack::after]:content-['']",
            "[&_.rt-SliderTrack::after]:absolute",
            "[&_.rt-SliderTrack::after]:top-0",
            "[&_.rt-SliderTrack::after]:bottom-0",
            "[&_.rt-SliderTrack::after]:w-px",
            "[&_.rt-SliderTrack::after]:bg-[#181818]",
            "[&_.rt-SliderTrack::after]:left-1/2",
            "[&_.rt-SliderTrack::after]:-translate-x-1/2",
            // ползунок
            "[&_.rt-SliderThumb]:w-1.5",
            "[&_.rt-SliderThumb]:h-1.5",
            "[&_.rt-SliderThumb]:[&_::after]:!bg-gradient-lightBlue",
            isLocked && "[&_.rt-SliderThumb]:[&_::after]:hidden",
          )}
        />
      </div>

      <div className="flex items-center mt-2 w-full">
        <div className="flex flex-col items-start w-[25%]">
          <div />
          <div className="h-[0.3125rem] w-px opacity-0" />
          {renderLabel(min)}
        </div>
        <div className="flex justify-between w-1/2">
          <div className="h-[0.3125rem] w-px bg-white/20" />
          <div className="h-[0.3125rem] w-px bg-white/20" />
          <div className="h-[0.3125rem] w-px bg-white/20" />
        </div>
        <div className="flex flex-col items-end w-[25%]">
          <div />
          <div className="h-[0.3125rem] w-px opacity-0" />
          {renderLabel(max)}
        </div>
      </div>
    </div>
  );
}
